<template>
  <div>
    <div class="px-4">
      <div class="d-flex justify-content-center mt-4" v-if="loaderActive && !model.name">
        <div class="spinner-border text-primary spinner-border" role="status">
          <span class="visually-hidden">Aguarde...</span>
        </div>
      </div>

      <div class="body p-4">
        <span class="text-primary h2 d-flex align-items-center">
          <button
            @click="$router.push('/cliente/nfse/servicos')"
            class="btn font-weight-bold btn-primary text-white btn-shadow me-3"
            v-prevent-click
          >
            <i class="fa fa-arrow-left"></i>
          </button>
          <span class="text-gray"> Editar Servi&ccedil;o</span>
        </span>

        <div class="card p-4 mt-4">
          <div class="p-2" v-show="!loaderActive || model.name">
            <form @submit.prevent="submitForm">
              <div class="row text-start">

                <div class="col-lg-6 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-name" class="form-label mb-2">
                    Nome do servi&ccedil;o*
                  </label>
                  <input
                    type="text"
                    id="input-name"
                    class="form-control"
                    placeholder="Mensalidade Sistema"
                    v-model="model.name"
                    required
                  />
                </div>

                <div class="col-lg-6 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-cnae" class="form-label mb-2">
                    CNAE
                  </label>
                  <input
                    type="text"
                    id="input-cnae"
                    class="form-control"
                    placeholder="6201-5/01"
                    v-model="model.cnae"
                  />
                </div>

                <div class="col-12 text-primary mb-4">
                  <label for="input-default-description" class="form-label mb-2">
                    Descri&ccedil;&atilde;o padr&atilde;o para NFS-e*
                  </label>
                  <textarea
                    id="input-default-description"
                    class="form-control"
                    rows="3"
                    placeholder="Mensalidade Sistema"
                    v-model="model.default_description"
                    required
                  ></textarea>
                </div>

                <div class="col-12 text-primary mb-4">
                  <nfse-classification-picker
                    v-model:classification="classification"
                    v-model:nbs="nbs"
                    v-model:ind-op="indOp"
                    v-model:class-trib="classTrib"
                  />
                </div>

                <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                  <label for="input-default-price" class="form-label mb-2">
                    Pre&ccedil;o padr&atilde;o
                  </label>
                  <input
                    type="number"
                    id="input-default-price"
                    class="form-control"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    v-model="model.default_price"
                  />
                </div>

              </div>

              <div class="d-flex gap-2 mt-4">
                <button
                  type="submit"
                  class="btn px-4 font-weight-bold btn-primary text-white btn-shadow"
                  :disabled="loaderActive"
                >
                  <span class="mx-4" v-if="!loaderActive">Salvar</span>
                  <span class="mx-4" v-else>Aguarde...</span>
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="goToProfiles"
                >
                  <i class="fas fa-city me-2"></i>Perfis municipais
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import api from "@/services/api";
import {
  getErrorMessage,
  buildNfseServicePayload,
  validateNfseServicePayload,
} from "@/utils/nfseHelpers";
import { useToast } from "vue-toastification";
import NfseClassificationPicker from "@/components/NfseClassificationPicker.vue";

const toast = useToast();

export default {
  name: "nfse-service-edit",
  components: { NfseClassificationPicker },
  data() {
    return {
      loaderActive: false,
      model: {
        name: "",
        default_description: "",
        cnae: "",
        default_price: "",
      },
      classification: null,
      nbs: null,
      indOp: null,
      classTrib: null,
    };
  },
  async mounted() {
    await this.loadService();
  },
  methods: {
    // Usa o preload da relação (service[relation]) quando presente; senão
    // procura o id (service[idField]) dentro das opções da classificação já
    // carregada — mesma regra para NBS, cIndOp e cClassTrib.
    hydrateOption(service, relation, idField, optionsKey) {
      if (service[relation]) return service[relation];
      const id = service[idField];
      if (!id) return null;
      const options = (service.classification && service.classification[optionsKey]) || [];
      const found = options.find((option) => String(option.id) === String(id));
      // Preserva o id mesmo sem achar a opção nas opções pré-carregadas (ex.:
      // opção inativa) — evita que buildNfseServicePayload apague o valor.
      return found || { id };
    },
    fillModel(service) {
      this.model.name = service.name || "";
      this.model.default_description = service.default_description || "";
      this.model.cnae = service.cnae || "";
      this.model.default_price = service.default_price ?? "";
      if (service.classification) {
        this.classification = service.classification;
      }
      this.nbs = this.hydrateOption(service, "nbs", "nfse_classification_nbs_id", "nbsOptions");
      this.indOp = this.hydrateOption(
        service,
        "indOp",
        "nfse_classification_ind_op_id",
        "indOpOptions"
      );
      this.classTrib = this.hydrateOption(
        service,
        "classTrib",
        "nfse_classification_class_trib_id",
        "classTribOptions"
      );
    },
    async loadService() {
      try {
        this.loaderActive = true;
        const { data } = await api.get(
          `/company/nfse/services/${this.$route.params.id}`
        );
        this.fillModel(data.data || data);
      } catch (e) {
        toast.error(
          getErrorMessage(e, "Ocorreu um erro ao carregar o serviço.")
        );
      } finally {
        this.loaderActive = false;
      }
    },
    async submitForm() {
      const payload = buildNfseServicePayload(this);
      const error = validateNfseServicePayload(payload);
      if (error) {
        toast.error(error);
        return;
      }

      try {
        this.loaderActive = true;
        await api.put(`/company/nfse/services/${this.$route.params.id}`, payload);
        toast.success("Serviço atualizado com sucesso.");
        this.$router.push("/cliente/nfse/servicos");
      } catch (e) {
        toast.error(
          getErrorMessage(e, "Ocorreu um erro ao salvar o serviço.")
        );
      } finally {
        this.loaderActive = false;
      }
    },
    goToProfiles() {
      this.$router.push(`/cliente/nfse/servicos/${this.$route.params.id}/municipios`);
    },
  },
};
</script>
